import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaShoppingCart, FaHeart } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import api from "../api";

export default function NewArrivals() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [selectedSizes, setSelectedSizes] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [addingToCart, setAddingToCart] = useState(null);
  const cream = "#F5EFE3";
  const creamLight = "#FBF8F1";
  const teal = "#0F766E";
  const tealDark = "#115E59";
  const brown = "#8B5E3C";

  const showToast = (message, duration = 2500) => {
    setToast(message);
    setTimeout(() => setToast(""), duration);
  };

  useEffect(() => {
    let mounted = true;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const collectedProducts = [];
        let page = 1;
        let nextUrl = "/api/products/?ordering=-id&page=1";
        while (nextUrl && collectedProducts.length < 10) {
          const response = await api.get(nextUrl);
          if (!mounted) return;
          const pageProducts = Array.isArray(response.data) ? response.data : Array.isArray(response.data?.results) ? response.data.results : [];
          collectedProducts.push(...pageProducts);
          if (Array.isArray(response.data)) { nextUrl = null; break; }
          if (response.data && Object.prototype.hasOwnProperty.call(response.data, "next")) {
            if (response.data.next) {
              try { const parsedNext = new URL(response.data.next); nextUrl = `${parsedNext.pathname}${parsedNext.search}`; }
              catch { nextUrl = response.data.next; }
            } else nextUrl = null;
          } else if (pageProducts.length > 0) { page += 1; nextUrl = `/api/products/?ordering=-id&page=${page}`; }
          else nextUrl = null;
          if (page > 20) nextUrl = null;
        }
        if (!mounted) return;
        const uniqueProducts = Array.from(new Map(collectedProducts.map((product) => [product.id ?? product.product_id, product])).values());
        const recentProducts = uniqueProducts.sort((a, b) => {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (aDate && bDate && aDate !== bDate) return bDate - aDate;
          return Number(b.id || 0) - Number(a.id || 0);
        }).slice(0, 10);
        setProducts(recentProducts);
        const initialSizes = {};
        recentProducts.forEach((product) => { if (Array.isArray(product.sizes) && product.sizes.length > 0) initialSizes[product.id] = product.sizes[0].id; });
        setSelectedSizes(initialSizes);
      } catch (error) {
        console.error("Error loading new arrivals:", error);
        setProducts([]);
        showToast("Unable to load new arrivals. Please try again.", 3000);
      } finally { if (mounted) setLoading(false); }
    };
    fetchProducts();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    try { setFavorites(JSON.parse(localStorage.getItem("favs") || "{}")); }
    catch { setFavorites({}); }
  }, []);

  const toggleFavorite = async (product) => {
    const localFavs = JSON.parse(localStorage.getItem("favs") || "{}");
    const newValue = !localFavs[product.id];
    localFavs[product.id] = newValue;
    localStorage.setItem("favs", JSON.stringify(localFavs));
    setFavorites({ ...localFavs });
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.access) { showToast(newValue ? "Added to favorites" : "Removed from favorites"); return; }
    try {
      await api.post("/api/favorites/toggle/", { product_id: product.id }, { headers: { Authorization: `Bearer ${user.access}` } });
      showToast(newValue ? "Added to favorites" : "Removed from favorites");
    } catch (error) { console.error("Favorite error:", error); showToast("Unable to update favorite"); }
  };

  const getPriceData = (size, product) => {
    const originalPrice = Number(size?.price ?? size?.original_price ?? size?.regular_price ?? product?.price ?? product?.original_price ?? product?.regular_price);
    if (!Number.isFinite(originalPrice)) return { originalPrice: null, salePrice: null, discountPercent: 0, hasDiscount: false };
    const rawSalePrice = size?.discount_price ?? size?.sale_price ?? size?.discounted_price ?? size?.final_price ?? product?.discount_price ?? product?.sale_price ?? product?.discounted_price ?? product?.final_price ?? null;
    let salePrice = rawSalePrice !== null && rawSalePrice !== undefined && rawSalePrice !== "" ? Number(rawSalePrice) : null;
    let discountPercent = Number(size?.discount_percent ?? size?.discount_percentage ?? size?.discount ?? size?.off_percent ?? product?.discount_percent ?? product?.discount_percentage ?? product?.discount ?? product?.off_percent ?? null);
    if (!Number.isFinite(salePrice) && Number.isFinite(discountPercent) && discountPercent > 0 && discountPercent < 100) salePrice = originalPrice - (originalPrice * discountPercent) / 100;
    const hasDiscount = Number.isFinite(salePrice) && salePrice >= 0 && salePrice < originalPrice;
    if (!hasDiscount) return { originalPrice, salePrice: originalPrice, discountPercent: 0, hasDiscount: false };
    discountPercent = Math.round(((originalPrice - salePrice) / originalPrice) * 100);
    return { originalPrice, salePrice, discountPercent, hasDiscount: true };
  };

  const getSelectedSize = (product) => {
    const sizes = Array.isArray(product?.sizes) ? product.sizes : [];
    if (sizes.length === 0) return null;
    const selectedSizeId = selectedSizes[product.id];
    return sizes.find((size) => String(size.id) === String(selectedSizeId)) || sizes[0];
  };

  const addToCart = async (product) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.access) { showToast("Please login first"); return; }
    const sizes = Array.isArray(product.sizes) ? product.sizes : [];
    const selectedSizeId = selectedSizes[product.id];
    if (!selectedSizeId) { showToast("Please select a size"); return; }
    const selectedSize = sizes.find((size) => String(size.id) === String(selectedSizeId));
    if (!selectedSize) { showToast("Please select a valid size"); return; }
    try {
      setAddingToCart(product.id);
      await api.post("/api/carts/add/", { product_id: product.id, size_id: selectedSize.id, quantity: 1 }, { headers: { Authorization: `Bearer ${user.access}` } });
      window.dispatchEvent(new Event("cartUpdated"));
      showToast("Added to cart");
    } catch (error) {
      console.error("Add to cart error:", error);
      showToast(error.response?.data?.detail || error.response?.data?.error || "Unable to add this product to cart.");
    } finally { setAddingToCart(null); }
  };

  const goToProduct = (product) => navigate(`/product/${product.id}`);
  const getProductImage = (product) => product?.sub_images?.[0]?.image || product?.image || "/placeholder.jpg";

  if (loading) {
    return <section className="py-10 sm:py-12 md:py-16" style={{ backgroundColor: creamLight }}><div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8"><div className="text-center mb-8"><div className="h-8 w-48 mx-auto rounded-lg animate-pulse" style={{ backgroundColor: cream }} /><div className="h-4 w-64 mx-auto mt-3 rounded-lg animate-pulse" style={{ backgroundColor: cream }} /></div><div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 md:gap-5">{Array(10).fill(0).map((_, index) => <div key={index} className="rounded-xl sm:rounded-2xl overflow-hidden animate-pulse" style={{ backgroundColor: cream }}><div className="h-44 sm:h-56 md:h-64" style={{ backgroundColor: "#E7DCC9" }} /><div className="p-3"><div className="h-4 rounded mb-2" style={{ backgroundColor: "#E7DCC9" }} /><div className="h-3 w-2/3 rounded" style={{ backgroundColor: "#E7DCC9" }} /></div></div>)}</div></div></section>;
  }

  return <section className="py-8 sm:py-10 md:py-14" style={{ backgroundColor: creamLight }}>
    {toast && <div className="fixed top-5 right-5 left-5 sm:left-auto px-5 py-3 rounded-xl shadow-xl z-50 text-white font-medium text-center" style={{ backgroundColor: teal }}>{toast}</div>}
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
      <div className="text-center mb-7 sm:mb-9 md:mb-10">
        <h2 className="home-typewriter text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: tealDark }}>New Arrivals</h2>
        <p className="mt-2 sm:mt-3 text-xs sm:text-sm md:text-base" style={{ color: brown }}>Discover our latest handcrafted rugs</p>
      </div>
      {products.length === 0 ? <div className="text-center py-16"><p className="text-gray-500 text-lg">No products found</p></div> : <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 md:gap-5">{products.map((product) => {
        const sizes = Array.isArray(product.sizes) ? product.sizes : [];
        const selectedSize = getSelectedSize(product);
        const priceData = getPriceData(selectedSize, product);
        const image = getProductImage(product);
        return <article key={product.id} className="group overflow-hidden rounded-xl sm:rounded-2xl border shadow-sm hover:shadow-xl transition-all duration-500" style={{ backgroundColor: cream, borderColor: "#E7DCC9" }}>
          <div className="relative overflow-hidden aspect-[4/3] cursor-pointer" onClick={() => goToProduct(product)}><img src={image} alt={product.name || "Handmade Afghan rug"} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />{product.is_sold && <div className="absolute inset-0 bg-black/45 flex items-center justify-center"><span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-gray-900">SOLD</span></div>}<button type="button" onClick={(e) => { e.stopPropagation(); toggleFavorite(product); }} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 shadow-md flex items-center justify-center">{favorites[product.id] ? <FaHeart className="text-red-500" /> : <FiHeart className="text-gray-700" />}</button></div>
          <div className="p-3 sm:p-4"><button type="button" onClick={() => goToProduct(product)} className="block w-full text-left"><h3 className="font-semibold text-sm sm:text-base line-clamp-2" style={{ color: tealDark }}>{product.name}</h3></button>
            {sizes.length > 0 && <select value={selectedSizes[product.id] || ""} onChange={(e) => setSelectedSizes((prev) => ({ ...prev, [product.id]: e.target.value }))} className="mt-3 w-full rounded-lg border bg-white px-2 py-2 text-xs sm:text-sm outline-none" style={{ borderColor: "#D8CDBD" }}><option value="">Select size</option>{sizes.map((size) => <option key={size.id} value={size.id}>{size.length_ft || size.length_cm} × {size.width_ft || size.width_cm}</option>)}</select>}
            <div className="mt-3 flex items-end justify-between gap-2"><div>{priceData.hasDiscount && <p className="text-xs text-gray-400 line-through">${Number(priceData.originalPrice).toFixed(2)}</p>}<p className="font-bold text-lg" style={{ color: teal }}>${Number(priceData.salePrice ?? priceData.originalPrice).toFixed(2)}</p></div><button type="button" disabled={product.is_sold || addingToCart === product.id} onClick={() => addToCart(product)} className="h-9 w-9 rounded-full text-white flex items-center justify-center disabled:opacity-40" style={{ backgroundColor: teal }}><FaShoppingCart className="text-sm" /></button></div>
          </div>
        </article>;
      })}</div>}
    </div>
  </section>;
}
